/**
 * UserDataGrid Component
 *
 * Displays a paginated table of users using MUI DataGrid with server-side pagination.
 * Includes columns for Email, Name, User id, Status, Role, and Actions.
 * The Actions column contains a "Resend invite" button for users with "Invited"
 * status and a "Remove" action (when the caller may manage members).
 *
 * @module components/UserDataGrid
 */

import { useState, useCallback, useMemo } from 'react';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import { Button, Box, IconButton, Stack, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import type { User, PaginationMeta, UserRole } from '../types';
import { mapUserStatus } from '../utils';
import { CopyableId } from './CopyableId';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for the UserDataGrid component.
 */
export interface UserDataGridProps {
  /** Array of user entities to display */
  users: User[];

  /** Pagination metadata (page, pageSize, totalCount) */
  pagination: PaginationMeta;

  /** Callback when page changes */
  onPageChange: (page: number) => void;

  /** Callback when page size changes */
  onPageSizeChange: (pageSize: number) => void;

  /** Callback when resend button is clicked for a user */
  onResendInvite: (userId: string) => Promise<void>;

  /**
   * Callback when the remove action is clicked for a user. The parent owns the
   * confirmation dialog, so the grid only requests the deletion (passing the
   * full {@link User} so the dialog can name them). Omit to hide the action
   * entirely.
   */
  onRequestDelete?: (user: User) => void;

  /**
   * Whether the current viewer may remove members from the targeted org. When
   * false (or `onRequestDelete` is omitted), the Remove action is not rendered.
   * Mirrors the same authority that gates the "Invite user" button.
   */
  canDelete?: boolean;

  /**
   * Callback when the role-toggle action is clicked for a user. The parent owns
   * the confirmation dialog (the target role is the opposite of the user's
   * current role), so the grid only requests the change. Omit to hide the
   * action entirely.
   */
  onRequestRoleChange?: (user: User) => void;

  /**
   * Whether the current viewer may change members' roles in the targeted org.
   * When false (or `onRequestRoleChange` is omitted), the role-toggle action is
   * not rendered. Same member-management authority that gates Remove.
   */
  canChangeRole?: boolean;

  /**
   * The current user's own id (the ID-token `sub`). The Remove action is
   * disabled for this user's own row — admins can't remove themselves. `null`
   * when unknown (no row is self-matched).
   */
  currentUserId?: string | null;

  /** Loading state for displaying skeleton */
  isLoading?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Available page size options for the DataGrid.
 */
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

/**
 * Locale text for empty state.
 */
const EMPTY_STATE_TEXT = 'No users found';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Formats the user's role for display.
 */
function formatRole(role: UserRole): string {
  return role === 'admin' ? 'Admin' : 'Member';
}

// =============================================================================
// UserDataGrid Component
// =============================================================================

/**
 * UserDataGrid - Displays a paginated table of users.
 *
 * Features:
 * - Server-side pagination with MUI DataGrid
 * - Columns: Email, Name, Status, Role, Actions
 * - Status column maps ZITADEL states to friendly labels
 * - Actions column with "Resend invite" button for invited users
 * - Per-row loading state for resend operations
 *
 * @example
 * ```tsx
 * function UsersPage() {
 *   const { users, pagination, setPage, setPageSize, resendInvite } = useUsersStore();
 *
 *   return (
 *     <UserDataGrid
 *       users={users}
 *       pagination={pagination}
 *       onPageChange={setPage}
 *       onPageSizeChange={setPageSize}
 *       onResendInvite={resendInvite}
 *     />
 *   );
 * }
 * ```
 */
export function UserDataGrid({
  users,
  pagination,
  onPageChange,
  onPageSizeChange,
  onResendInvite,
  onRequestDelete,
  canDelete = false,
  onRequestRoleChange,
  canChangeRole = false,
  currentUserId = null,
  isLoading = false,
}: UserDataGridProps) {
  // Track which user ID has a pending resend request
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  // Whether to render the Remove action: the viewer must have delete authority
  // AND the parent must provide a handler to open its confirmation dialog.
  const showDelete = canDelete && typeof onRequestDelete === 'function';

  // Whether to render the role-toggle action: same shape as the Remove gate.
  const showRoleChange =
    canChangeRole && typeof onRequestRoleChange === 'function';

  /**
   * Handles resend invite button click for a specific user.
   * Sets per-row loading state during the operation.
   */
  const handleResendInvite = useCallback(
    async (userId: string) => {
      setResendingUserId(userId);
      try {
        await onResendInvite(userId);
      } finally {
        setResendingUserId(null);
      }
    },
    [onResendInvite],
  );

  /**
   * Handles pagination model changes from DataGrid.
   */
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      // DataGrid uses 0-based page index, but our API uses 1-based
      const newPage = model.page + 1;
      const newPageSize = model.pageSize;

      // Only call the appropriate callback if the value changed
      if (newPage !== pagination.page) {
        onPageChange(newPage);
      }
      if (newPageSize !== pagination.pageSize) {
        onPageSizeChange(newPageSize);
      }
    },
    [pagination.page, pagination.pageSize, onPageChange, onPageSizeChange],
  );

  /**
   * Column definitions for the DataGrid.
   */
  const columns: GridColDef<User>[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.5,
        minWidth: 200,
      },
      {
        field: 'displayName',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) => {
          // Use displayName, or construct from givenName/familyName, or empty
          if (row.displayName) {
            return row.displayName;
          }
          if (row.givenName || row.familyName) {
            return [row.givenName, row.familyName].filter(Boolean).join(' ');
          }
          return '';
        },
      },
      {
        // REQ-AUTH-078: the portal is an internal admin surface, so the ZITADEL
        // account id is on the row. It is the value a token carries as `sub`,
        // and the input the external-access grant takes (D2), so it is shown
        // here rather than looked up in the ZITADEL console.
        field: 'id',
        headerName: 'User id',
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: (params) => <CopyableId value={params.row.id} />,
      },
      {
        field: 'state',
        headerName: 'Status',
        width: 100,
        valueGetter: (_value, row) => mapUserStatus(row.state),
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 100,
        valueGetter: (_value, row) => formatRole(row.role),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const status = mapUserStatus(params.row.state);
          const isInvited = status === 'Invited';
          const isResending = resendingUserId === params.row.id;

          // Resend is only meaningful for invited (not-yet-active) users.
          const resendButton = isInvited ? (
            <Button
              size="small"
              variant="outlined"
              disabled={isResending}
              onClick={() => handleResendInvite(params.row.id)}
              data-testid={`resend-invite-${params.row.id}`}
            >
              {isResending ? 'Sending...' : 'Resend invite'}
            </Button>
          ) : null;

          // Remove is shown when the viewer may manage members. The viewer's
          // own row is disabled — admins can't remove themselves (the backend
          // also enforces this; this is a friendly client-side guard rail).
          const isSelf =
            currentUserId !== null && params.row.id === currentUserId;

          // Role toggle: promote a member / demote an admin. The viewer's own
          // row is disabled — you can't change your own role (backend enforces
          // this too; 422 CANNOT_MODIFY_SELF).
          const isAdmin = params.row.role === 'admin';
          const roleButton = showRoleChange ? (
            <Tooltip
              title={
                isSelf
                  ? "You can't change your own role"
                  : isAdmin
                    ? 'Change to member'
                    : 'Make admin'
              }
            >
              {/* span wrapper so the tooltip still shows on a disabled button */}
              <span>
                <IconButton
                  size="small"
                  aria-label={
                    isAdmin
                      ? `Change ${params.row.email} to member`
                      : `Make ${params.row.email} an admin`
                  }
                  disabled={isSelf}
                  onClick={() => onRequestRoleChange?.(params.row)}
                  data-testid={`change-role-${params.row.id}`}
                >
                  {isAdmin ? (
                    <PersonRemoveOutlinedIcon fontSize="small" />
                  ) : (
                    <AdminPanelSettingsOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null;

          const deleteButton = showDelete ? (
            <Tooltip
              title={isSelf ? "You can't remove yourself" : 'Remove user'}
            >
              {/* span wrapper so the tooltip still shows on a disabled button */}
              <span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`Remove ${params.row.email}`}
                  disabled={isSelf}
                  onClick={() => onRequestDelete?.(params.row)}
                  data-testid={`delete-user-${params.row.id}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null;

          if (!resendButton && !roleButton && !deleteButton) {
            return null;
          }

          return (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                height: '100%',
              }}
            >
              {resendButton}
              {roleButton}
              {deleteButton}
            </Stack>
          );
        },
      },
    ],
    [
      resendingUserId,
      handleResendInvite,
      showDelete,
      showRoleChange,
      currentUserId,
      onRequestDelete,
      onRequestRoleChange,
    ],
  );

  /**
   * Pagination model for DataGrid (0-based page index).
   */
  const paginationModel: GridPaginationModel = {
    page: pagination.page - 1, // Convert from 1-based to 0-based
    pageSize: pagination.pageSize,
  };

  return (
    <Box sx={{ width: '100%', height: 'auto' }} data-testid="user-data-grid">
      <DataGrid
        rows={users}
        columns={columns}
        loading={isLoading}
        paginationMode="server"
        rowCount={pagination.totalCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableRowSelectionOnClick
        autoHeight
        localeText={{
          noRowsLabel: EMPTY_STATE_TEXT,
        }}
        sx={{
          // Ensure proper minimum height for empty state
          '& .MuiDataGrid-main': {
            minHeight: 200,
          },
        }}
      />
    </Box>
  );
}
